/**
 * ProactivePlumber.com Landing Page (React + TypeScript + React-Bootstrap)
 * Version: v09
 * Date: 2025-12-23 (America/Denver)
 *
 * v09 updates:
 * - Preferred contact: multi-select (checkboxes) -> string[]
 * - Best time: multi-select (checkboxes) -> string[]
 * - Externalized payload schema + toWirePayload into ./payload.ts
 * - Externalized submit logic into ./submitProactivePlumberLead.ts
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  ListGroup,
  Navbar,
  Nav,
  Row,
  Spinner,
  Carousel,
} from "react-bootstrap";
import {
  CheckCircleFill,
  DropletFill,
  GeoAltFill,
  StarFill,
  TelephoneFill,
} from "react-bootstrap-icons";

import type { LeadFormState } from "./plumberPayload";
import { toWirePayload } from "./plumberPayload";
import { postProactivePlumberLead } from "./submitProactivePlumberData";

/* ============================================================
   DEV TOGGLES
============================================================ */
const ENABLE_LEAD_FORM = true; // set false to hide the entire form card + nav CTA
const ENABLE_ZIP_GATE = true; // set false to show full form immediately (if ENABLE_LEAD_FORM true)

/* ============================================================
   SECTION THEMES (Easy Customization)
============================================================ */
type SectionKey = "hero" | "how" | "plans" | "testimonials" | "faq" | "footer";

type SectionTheme = {
  bg: string;
  text: string;
  subtleText: string;
  cardBg: string;
  cardBorder: string;
  accent: string;
  divider: string;
  heroOverlayTop: string;
  heroOverlayBottom: string;
  heroMaxHeight: number;
};

export const SECTION_THEME: Record<SectionKey, SectionTheme> = {
  hero: {
    bg: "linear-gradient(180deg, rgba(67, 68, 70, 0.18), rgba(140,146,172,.35))",
    text: "#36454F",
    subtleText: "rgba(54,69,79,.75)",
    cardBg: "rgba(242, 243, 245, 0.96)",
    cardBorder: "rgba(54,69,79,.25)",
    accent: "#8C92AC",
    divider: "rgba(54,69,79,.18)",
    heroOverlayTop: "rgba(2, 2, 2, 0.55)",
    heroOverlayBottom: "rgba(54,69,79,.30)",
    heroMaxHeight: 760,
  },

  how: {
    bg: "#a7aaacff",
    text: "#36454F",
    subtleText: "rgba(54,69,79,.70)",
    cardBg: "rgba(242, 243, 245, 0.96)",
    cardBorder: "rgba(140,146,172,.35)",
    accent: "#6D8196",
    divider: "rgba(54,69,79,.15)",
    heroOverlayTop: "rgba(0,0,0,0)",
    heroOverlayBottom: "rgba(0,0,0,0)",
    heroMaxHeight: 0,
  },

  plans: {
    bg: "#84888bff",
    text: "#36454F",
    subtleText: "rgba(54,69,79,.70)",
    cardBg: "rgba(242, 243, 245, 0.96)",
    cardBorder: "rgba(140,146,172,.35)",
    accent: "#8C92AC",
    divider: "rgba(54,69,79,.15)",
    heroOverlayTop: "rgba(0,0,0,0)",
    heroOverlayBottom: "rgba(0,0,0,0)",
    heroMaxHeight: 0,
  },

  testimonials: {
    bg: "#a7aaacff",
    text: "#36454F",
    subtleText: "rgba(54,69,79,.70)",
    cardBg: "rgba(242, 243, 245, 0.96)",
    cardBorder: "rgba(109,129,150,.35)",
    accent: "#6D8196",
    divider: "rgba(54,69,79,.15)",
    heroOverlayTop: "rgba(0,0,0,0)",
    heroOverlayBottom: "rgba(0,0,0,0)",
    heroMaxHeight: 0,
  },

  faq: {
    bg: "#84888bff",
    text: "#36454F",
    subtleText: "rgba(54,69,79,.70)",
    cardBg: "rgba(242, 243, 245, 0.96)",
    cardBorder: "rgba(140,146,172,.35)",
    accent: "#8C92AC",
    divider: "rgba(54,69,79,.15)",
    heroOverlayTop: "rgba(0,0,0,0)",
    heroOverlayBottom: "rgba(0,0,0,0)",
    heroMaxHeight: 0,
  },

  footer: {
    bg: "linear-gradient(180deg, rgba(54,69,79,1), rgba(109,129,150,1))",
    text: "rgba(250,240,221,.95)",
    subtleText: "rgba(250,240,221,.75)",
    cardBg: "rgba(250,240,221,.08)",
    cardBorder: "rgba(250,240,221,.18)",
    accent: "#FAF0DD",
    divider: "rgba(250,240,221,.22)",
    heroOverlayTop: "rgba(0,0,0,0)",
    heroOverlayBottom: "rgba(0,0,0,0)",
    heroMaxHeight: 0,
  },
};

/* ============================================================
   HERO SLIDES
============================================================ */
type HeroSlide = {
  src: string;
  alt: string;
  title?: string;
  subtitle?: string;
  description?: string;
};

const HERO_CAROUSEL_SLIDES: HeroSlide[] = [
  {
    src: "/images/proactive-plumber/AdobeStock_1600264123_KitchenSinkNotes.jpg",
    alt: "Professional plumber inspecting pipes under a kitchen sink",
    title: "Stop surprise leaks, water damage, and “weekend emergencies.”",
    subtitle: "Get a proactive plumbing checkup",
    description:
      "We help homeowners and property managers reduce plumbing failures with scheduled inspections, preventive maintenance, and priority response when something breaks",
  },
  {
    src: "/images/proactive-plumber/AdobeStock_1600264123_KitchenSinkNotes.jpg",
    alt: "Professional plumber inspecting pipes under a kitchen sink",
    title: "Catch Small Leaks Early",
    subtitle: "Prevent water damage before it starts",
    description:
      "Our proactive inspections find slow drips, worn connections, and hidden moisture before they turn into costly repairs.",
  },
  {
    src: "/images/proactive-plumber/AdobeStock_1421918098_WaterHeater.jpg",
    alt: "Plumber servicing and flushing a residential water heater",
    title: "Protect Your Water Heater",
    subtitle: "Extend lifespan and improve efficiency",
    description:
      "Routine water heater checks and flushes reduce breakdowns, improve performance, and help you avoid cold-water surprises.",
  },
  {
    src: "/images/proactive-plumber/AdobeStock_54345414_Bathroom.jpg",
    alt: "Modern bathroom faucet with plumber performing maintenance",
    title: "Fixture & Valve Tune-Ups",
    subtitle: "Stop drips, save water",
    description:
      "From faucets to shutoff valves, we tighten, test, and tune the components that most often fail in everyday homes.",
  },
  {
    src: "/images/proactive-plumber/AdobeStock_786057663_Pipes2.jpg",
    alt: "Plumber inspecting drain and sewer line",
    title: "Keep Drains Flowing",
    subtitle: "Avoid clogs and backups",
    description:
      "We check high-risk drains and recommend preventive care to help you avoid slow drains, odors, and messy backups.",
  },
  {
    src: "/images/proactive-plumber/AdobeStock_644371711_Customer.jpg",
    alt: "Friendly plumber talking with homeowner",
    title: "Your Proactive Plumbing Partner",
    subtitle: "Peace of mind year-round",
    description:
      "With scheduled checkups and priority service, we help homeowners and property managers stay ahead of plumbing problems.",
  },
];

/* ============================================================
   BRAND + FORM CONFIG
============================================================ */
type SubmitState = "idle" | "submitting" | "success" | "error";
type LeadStep = "zip" | "full";

const VERSION = "v09";

const BRAND = {
  name: "TheProactivePlumber.com",
  phoneDisplay: "(303) 777-2222",
  phoneHref: "tel:3037772222",
};

const SERVICE_TYPES = [
  "Preventive Plumbing Checkup",
  "Leak Detection / Water Pressure Issues",
  "Water Heater Flush / Service",
  "Drain & Sewer Preventive Maintenance",
  "Toilet / Faucet / Fixture Tune-Up",
  "Sump Pump / Basement Protection",
  "Emergency Repair (same-day)",
];

const URGENCY_LEVELS = [
  "Not urgent — planning",
  "Soon — within 1–2 weeks",
  "Urgent — within 24–48 hours",
  "Emergency!",
];

const CONTACT_PREFS = ["Call", "Text", "Email"];
const PREFERRED_TIMES = ["Morning", "Midday", "Afternoon", "Evening", "Anytime"];

/* ============================================================
   UTILS
============================================================ */
function isEmailValid(v: string) {
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}
function isPhoneLikelyValid(v: string) {
  const digits = (v || "").replace(/\D/g, "");
  return digits.length >= 10;
}
function zipLooksValid(v: string) {
  return /^\d{5}(-\d{4})?$/.test((v || "").trim());
}
function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}
function toggleInArray(current: string[], value: string) {
  if (current.includes(value)) return current.filter((x) => x !== value);
  return [...current, value];
}

/* ============================================================
   HERO BACKGROUND CAROUSEL
============================================================ */
function HeroBackgroundCarousel(props: {
  slides: HeroSlide[];
  maxHeightPx: number;
  overlayTop: string;
  overlayBottom: string;
  activeIndex: number;
  onSelect: (selectedIndex: number) => void;
}) {
  const { slides, maxHeightPx, overlayTop, overlayBottom, activeIndex, onSelect } = props;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        maxHeight: maxHeightPx ? `${maxHeightPx}px` : undefined,
      }}
    >
      <Carousel
        controls
        indicators
        fade
        interval={6500}
        pause={false}
        touch
        activeIndex={activeIndex}
        onSelect={(i) => onSelect(i)}
        style={{ height: "100%" }}
      >
        {slides.map((s, idx) => (
          <Carousel.Item key={`${s.src}-${idx}`} style={{ height: "100%" }}>
            <img
              src={s.src}
              alt={s.alt}
              style={{
                width: "100%",
                height: maxHeightPx ? `${maxHeightPx}px` : "100%",
                objectFit: "cover",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(180deg, ${overlayTop}, ${overlayBottom})`,
              }}
            />
          </Carousel.Item>
        ))}
      </Carousel>
    </div>
  );
}

/* ============================================================
   MULTI-SELECT CHECKBOX GROUP
============================================================ */
function CheckboxMultiSelectGroup(props: {
  name: string;
  options: string[];
  values: string[];
  onChange: (next: string[]) => void;
  onBlur?: () => void;
  isInvalid?: boolean;
  requiredErrorText?: string;
}) {
  const { name, options, values, onChange, onBlur, isInvalid, requiredErrorText } = props;

  return (
    <div
      role="group"
      aria-label={name}
      style={{
        border: isInvalid ? "1px solid rgba(220, 53, 69, .45)" : "1px solid rgba(0,0,0,.08)",
        borderRadius: 16,
        padding: 10,
        background: "rgba(255,255,255,.65)",
      }}
      onBlur={onBlur}
    >
      <Row className="g-2">
        {options.map((opt) => (
          <Col xs={12} key={opt}>
            <Form.Check
              type="checkbox"
              id={`${name}-${opt}`}
              checked={values.includes(opt)}
              onChange={() => onChange(toggleInArray(values, opt))}
              label={opt}
            />
          </Col>
        ))}
      </Row>

      {isInvalid ? (
        <div className="invalid-feedback d-block" style={{ marginTop: 6 }}>
          {requiredErrorText || "Please make at least one selection."}
        </div>
      ) : null}
    </div>
  );
}

/* ============================================================
   PAGE
============================================================ */
function ProactivePlumber_Landing_2025_12_23_chat_v09() {
  const hero = SECTION_THEME.hero;
  const how = SECTION_THEME.how;
  const plans = SECTION_THEME.plans;
  const testimonials = SECTION_THEME.testimonials;
  const faq = SECTION_THEME.faq;
  const footer = SECTION_THEME.footer;

  const HERO_KICKER = "Prevent problems before they become emergencies";

  const [activeHeroIndex, setActiveHeroIndex] = useState<number>(0);
  const activeSlide = HERO_CAROUSEL_SLIDES[Math.min(activeHeroIndex, HERO_CAROUSEL_SLIDES.length - 1)];

  useEffect(() => {
    if (activeHeroIndex < 0) setActiveHeroIndex(0);
  }, [activeHeroIndex]);

  const initialForm: LeadFormState = useMemo(
    () => ({
      fullName: "",
      phone: "",
      email: "",
      addressOrZip: "",
      serviceType: SERVICE_TYPES[0],
      urgency: URGENCY_LEVELS[0],

      // Multi-select defaults
      preferredContact: ["Call"],
      preferredTime: ["Anytime"],

      notes: "",
      consentToText: true,
    }),
    []
  );

  const [form, setForm] = useState<LeadFormState>(initialForm);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState<string>("");

  const [leadStep, setLeadStep] = useState<LeadStep>(ENABLE_ZIP_GATE ? "zip" : "full");
  const [zipGateValue, setZipGateValue] = useState<string>("");
  const [zipGateTouched, setZipGateTouched] = useState<boolean>(false);

  const requiredErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    if (!form.fullName.trim()) errs.fullName = "Please enter your name.";
    if (!form.phone.trim()) errs.phone = "Please enter your phone number.";
    else if (!isPhoneLikelyValid(form.phone)) errs.phone = "Please enter a valid 10-digit phone number.";
    if (form.email && !isEmailValid(form.email)) errs.email = "Please enter a valid email address.";
    if (!form.addressOrZip.trim()) errs.addressOrZip = "Please enter your ZIP code (or service address).";
    if (!form.serviceType) errs.serviceType = "Please choose a service type.";
    if (!form.urgency) errs.urgency = "Please choose urgency.";

    // Multi-select required: Preferred contact must have at least 1
    if (!form.preferredContact || form.preferredContact.length === 0) {
      errs.preferredContact = "Please choose at least one contact method.";
    }

    // Best time is optional (keep it optional)
    return errs;
  }, [form]);

  const canSubmit = Object.keys(requiredErrors).length === 0 && submitState !== "submitting";

  function setField<K extends keyof LeadFormState>(key: K, value: LeadFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }
  function markTouched(key: keyof LeadFormState) {
    setTouched((prev) => ({ ...prev, [String(key)]: true }));
  }

  function resetAll() {
    setForm(initialForm);
    setTouched({});
    setSubmitState("idle");
    setSubmitError("");
    setLeadStep(ENABLE_ZIP_GATE ? "zip" : "full");
    setZipGateValue("");
    setZipGateTouched(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");

    const toTouch: (keyof LeadFormState)[] = [
      "fullName",
      "phone",
      "email",
      "addressOrZip",
      "serviceType",
      "urgency",
      "preferredContact",
    ];
    setTouched((prev) => {
      const next = { ...prev };
      toTouch.forEach((k) => (next[String(k)] = true));
      return next;
    });

    if (Object.keys(requiredErrors).length > 0) return;

    setSubmitState("submitting");
    try {
      const payload = toWirePayload({
        version: VERSION,
        sourceDomain: BRAND.name,
        state: form,
      });
      await postProactivePlumberLead(payload);
      setSubmitState("success");
    } catch (err: any) {
      setSubmitState("error");
      setSubmitError(err?.message || "Something went wrong. Please try again or call us.");
    }
  }

  return (
    <div>
      {/* NAV */}
      <Navbar
        expand="lg"
        className="py-3"
        style={{
          background: "rgba(255,255,255,.86)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(0,0,0,.06)",
        }}
      >
        <Container>
          <Navbar.Brand className="fw-bold d-flex align-items-center gap-2" style={{ letterSpacing: 0.2 }}>
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-4"
              style={{ width: 38, height: 38, background: "rgba(13,110,253,.14)" }}
              aria-hidden
            >
              <DropletFill color={hero.accent} />
            </span>
            <span style={{ color: hero.text }}>{BRAND.name}</span>
            <Badge bg="light" text="primary" className="ms-2 border">
              Proactive Plumbing
            </Badge>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="mainnav" />
          <Navbar.Collapse id="mainnav">
            <Nav className="ms-auto align-items-lg-center gap-lg-3">
              <Nav.Link onClick={() => scrollToId("how")}>How it works</Nav.Link>
              <Nav.Link onClick={() => scrollToId("plans")}>Plans</Nav.Link>
              <Nav.Link onClick={() => scrollToId("faq")}>FAQ</Nav.Link>
              {ENABLE_LEAD_FORM && <Nav.Link onClick={() => scrollToId("lead")}>Service Request</Nav.Link>}

              <div className="d-flex align-items-center gap-2 ms-lg-2">
                <a className="text-decoration-none fw-semibold" style={{ color: hero.text }} href={BRAND.phoneHref}>
                  <TelephoneFill className="me-2" />
                  {BRAND.phoneDisplay}
                </a>
                {ENABLE_LEAD_FORM && (
                  <Button variant="primary" className="rounded-4" onClick={() => scrollToId("lead")}>
                    Service Request
                  </Button>
                )}
              </div>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section
        style={{
          position: "relative",
          minHeight: hero.heroMaxHeight ? `${hero.heroMaxHeight}px` : "680px",
          background: hero.bg,
        }}
      >
        <HeroBackgroundCarousel
          slides={HERO_CAROUSEL_SLIDES}
          maxHeightPx={hero.heroMaxHeight}
          overlayTop={hero.heroOverlayTop}
          overlayBottom={hero.heroOverlayBottom}
          activeIndex={activeHeroIndex}
          onSelect={(i) => setActiveHeroIndex(i)}
        />

        <Container style={{ position: "relative", zIndex: 2 }} className="py-5">
          <Row className="align-items-center g-4">
            {/* LEFT */}
            <Col lg={ENABLE_LEAD_FORM ? 7 : 12}>
              <div className="mb-3">
                <Badge bg="primary" className="rounded-pill px-3 py-2">
                  {HERO_KICKER}
                </Badge>
              </div>

              <h1 className="display-5 fw-bold lh-sm" style={{ color: "rgba(255,255,255,.97)" }}>
                {activeSlide?.title || "Get a proactive plumbing checkup."}
              </h1>

              {activeSlide?.subtitle && (
                <div className="mt-2 fw-semibold" style={{ color: "rgba(255,255,255,.88)", fontSize: 16, letterSpacing: 0.2 }}>
                  {activeSlide.subtitle}
                </div>
              )}

              {activeSlide?.description && (
                <p className="lead mt-3" style={{ color: "rgba(255,255,255,.84)" }}>
                  {activeSlide.description}
                </p>
              )}

              {!ENABLE_LEAD_FORM && (
                <div className="mt-4">
                  <Button variant="primary" size="lg" className="rounded-4" href={BRAND.phoneHref}>
                    Call {BRAND.phoneDisplay}
                  </Button>
                </div>
              )}
            </Col>

            {/* RIGHT: LEAD CARD */}
            {ENABLE_LEAD_FORM && (
              <Col lg={5} id="lead">
                <Card
                  className="border-0 shadow rounded-4"
                  style={{
                    background: hero.cardBg,
                    border: `1px solid ${hero.cardBorder}`,
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                      <div>
                        <h2 className="h4 fw-bold mb-1">Service Request</h2>
                        <div className="small" style={{ color: hero.subtleText }}>
                          Start with your ZIP code to confirm service availability.
                        </div>
                      </div>
                      <Badge bg="light" text="primary" className="border">
                        {VERSION}
                      </Badge>
                    </div>

                    <hr style={{ borderColor: "rgba(0,0,0,.08)" }} />

                    {submitState === "success" ? (
                      <div>
                        <Alert variant="success" className="rounded-4">
                          <div className="d-flex align-items-center gap-2 fw-semibold">
                            <CheckCircleFill />
                            Request received!
                          </div>
                          <div className="small mt-2">
                            We’ll reach out shortly. If urgent, call{" "}
                            <a className="fw-semibold" href={BRAND.phoneHref}>
                              {BRAND.phoneDisplay}
                            </a>
                            .
                          </div>
                        </Alert>
                        <Button variant="outline-primary" className="rounded-4 w-100" onClick={resetAll}>
                          Submit another request
                        </Button>
                      </div>
                    ) : (
                      <>
                        {/* STEP 1: ZIP ONLY */}
                        {leadStep === "zip" && (
                          <Form
                            onSubmit={(e) => {
                              e.preventDefault();
                              setZipGateTouched(true);
                              if (!zipLooksValid(zipGateValue)) return;

                              setForm((prev) => ({ ...prev, addressOrZip: zipGateValue.trim() }));
                              setLeadStep("full");
                            }}
                          >
                            <Form.Label className="fw-semibold">Service ZIP Code</Form.Label>
                            <InputGroup>
                              <InputGroup.Text className="rounded-start-4">
                                <GeoAltFill />
                              </InputGroup.Text>
                              <Form.Control
                                value={zipGateValue}
                                onChange={(e) => setZipGateValue(e.target.value)}
                                onBlur={() => setZipGateTouched(true)}
                                placeholder="e.g., 80301"
                                className="rounded-end-4"
                                isInvalid={zipGateTouched && !zipLooksValid(zipGateValue)}
                                inputMode="numeric"
                                autoComplete="postal-code"
                              />
                              <Form.Control.Feedback type="invalid">Please enter a valid ZIP code.</Form.Control.Feedback>
                            </InputGroup>

                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" className="rounded-4">
                                Service Request
                              </Button>
                            </div>

                            <div className="small text-center mt-3" style={{ color: "rgba(11,19,32,.70)" }}>
                              Prefer to talk now?{" "}
                              <a className="fw-semibold" href={BRAND.phoneHref}>
                                Call {BRAND.phoneDisplay}
                              </a>
                            </div>
                          </Form>
                        )}

                        {/* STEP 2: FULL FORM */}
                        {leadStep === "full" && (
                          <Form onSubmit={handleSubmit}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <div className="small" style={{ color: hero.subtleText }}>
                                ZIP: <span className="fw-semibold">{form.addressOrZip || zipGateValue}</span>
                              </div>
                              {ENABLE_ZIP_GATE && (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="rounded-4"
                                  type="button"
                                  onClick={() => {
                                    setZipGateValue(form.addressOrZip || zipGateValue);
                                    setLeadStep("zip");
                                  }}
                                >
                                  Change ZIP
                                </Button>
                              )}
                            </div>

                            <Row className="g-3">
                              <Col xs={12}>
                                <Form.Label className="fw-semibold">Name</Form.Label>
                                <Form.Control
                                  value={form.fullName}
                                  onChange={(e) => setField("fullName", e.target.value)}
                                  onBlur={() => markTouched("fullName")}
                                  placeholder="Full name"
                                  className="rounded-4"
                                  isInvalid={Boolean(touched.fullName && requiredErrors.fullName)}
                                />
                                <Form.Control.Feedback type="invalid">{requiredErrors.fullName}</Form.Control.Feedback>
                              </Col>

                              <Col xs={12}>
                                <Form.Label className="fw-semibold">Phone</Form.Label>
                                <InputGroup>
                                  <InputGroup.Text className="rounded-start-4">
                                    <TelephoneFill />
                                  </InputGroup.Text>
                                  <Form.Control
                                    value={form.phone}
                                    onChange={(e) => setField("phone", e.target.value)}
                                    onBlur={() => markTouched("phone")}
                                    placeholder="(555) 555-5555"
                                    className="rounded-end-4"
                                    isInvalid={Boolean(touched.phone && requiredErrors.phone)}
                                  />
                                  <Form.Control.Feedback type="invalid">{requiredErrors.phone}</Form.Control.Feedback>
                                </InputGroup>

                                <Form.Check
                                  className="mt-2 small"
                                  type="checkbox"
                                  checked={form.consentToText}
                                  onChange={(e) => setField("consentToText", e.target.checked)}
                                  label="You may text me about scheduling (standard message rates may apply)."
                                />
                              </Col>

                              <Col xs={12}>
                                <Form.Label className="fw-semibold">Email (optional)</Form.Label>
                                <Form.Control
                                  value={form.email}
                                  onChange={(e) => setField("email", e.target.value)}
                                  onBlur={() => markTouched("email")}
                                  placeholder="name@email.com"
                                  className="rounded-4"
                                  isInvalid={Boolean(touched.email && requiredErrors.email)}
                                />
                                <Form.Control.Feedback type="invalid">{requiredErrors.email}</Form.Control.Feedback>
                              </Col>

                              <Col xs={12}>
                                <Form.Label className="fw-semibold">ZIP code or service address</Form.Label>
                                <Form.Control
                                  value={form.addressOrZip}
                                  onChange={(e) => setField("addressOrZip", e.target.value)}
                                  onBlur={() => markTouched("addressOrZip")}
                                  placeholder="ZIP or address"
                                  className="rounded-4"
                                  isInvalid={Boolean(touched.addressOrZip && requiredErrors.addressOrZip)}
                                  autoComplete="postal-code"
                                />
                                <Form.Control.Feedback type="invalid">{requiredErrors.addressOrZip}</Form.Control.Feedback>
                              </Col>

                              {/* Service (keep single-select for now) */}
                              <Col md={6}>
                                <Form.Label className="fw-semibold">Service</Form.Label>
                                <Form.Select
                                  value={form.serviceType}
                                  onChange={(e) => setField("serviceType", e.target.value)}
                                  onBlur={() => markTouched("serviceType")}
                                  className="rounded-4"
                                  isInvalid={Boolean(touched.serviceType && requiredErrors.serviceType)}
                                >
                                  {SERVICE_TYPES.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{requiredErrors.serviceType}</Form.Control.Feedback>
                              </Col>

                              <Col md={6}>
                                <Form.Label className="fw-semibold">Urgency</Form.Label>
                                <Form.Select
                                  value={form.urgency}
                                  onChange={(e) => setField("urgency", e.target.value)}
                                  onBlur={() => markTouched("urgency")}
                                  className="rounded-4"
                                  isInvalid={Boolean(touched.urgency && requiredErrors.urgency)}
                                >
                                  {URGENCY_LEVELS.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{requiredErrors.urgency}</Form.Control.Feedback>
                              </Col>

                              {/* Preferred contact (multi) */}
                              <Col md={6}>
                                <Form.Label className="fw-semibold">Preferred contact</Form.Label>
                                <CheckboxMultiSelectGroup
                                  name="preferredContact"
                                  options={CONTACT_PREFS}
                                  values={form.preferredContact}
                                  onChange={(next) => setField("preferredContact", next)}
                                  onBlur={() => markTouched("preferredContact")}
                                  isInvalid={Boolean(touched.preferredContact && requiredErrors.preferredContact)}
                                  requiredErrorText={requiredErrors.preferredContact}
                                />
                              </Col>

                              {/* Best time (multi, optional) */}
                              <Col md={6}>
                                <Form.Label className="fw-semibold">Best time</Form.Label>
                                <CheckboxMultiSelectGroup
                                  name="preferredTime"
                                  options={PREFERRED_TIMES}
                                  values={form.preferredTime}
                                  onChange={(next) => setField("preferredTime", next)}
                                />
                              </Col>

                              <Col xs={12}>
                                <Form.Label className="fw-semibold">Problem Description ( optional )</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  value={form.notes}
                                  onChange={(e) => setField("notes", e.target.value)}
                                  placeholder="Any details you want us to know..."
                                  className="rounded-4"
                                />
                              </Col>

                              {submitState === "error" && (
                                <Col xs={12}>
                                  <Alert variant="danger" className="rounded-4 mb-0">
                                    {submitError || "Something went wrong. Please try again."}
                                  </Alert>
                                </Col>
                              )}

                              <Col xs={12} className="mt-2">
                                <Button type="submit" variant="primary" size="lg" className="rounded-4 w-100" disabled={!canSubmit}>
                                  {submitState === "submitting" ? (
                                    <>
                                      <Spinner size="sm" className="me-2" />
                                      Sending…
                                    </>
                                  ) : (
                                    "Request Scheduling"
                                  )}
                                </Button>

                                <div className="small text-center mt-2" style={{ color: "rgba(11,19,32,.70)" }}>
                                  Prefer to talk now?{" "}
                                  <a className="fw-semibold" href={BRAND.phoneHref}>
                                    Call {BRAND.phoneDisplay}
                                  </a>
                                </div>
                              </Col>
                            </Row>
                          </Form>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" style={{ background: how.bg, color: how.text }}>
        <Container className="py-5">
          <Row className="g-4 mb-3">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How it works</h2>
              <div style={{ color: how.subtleText }}>
                Proactive checkups reduce surprises and help you plan fixes on your schedule—not during an emergency.
              </div>
            </Col>
          </Row>

          <Row className="g-4">
            {[
              {
                n: "1",
                title: "We inspect the failure points",
                body:
                  "We check supply lines, shutoffs, pressure, fixtures, exposed valves, water heater basics, and visible drains.",
              },
              {
                n: "2",
                title: "You get a simple action plan",
                body: "Clear checklist + priority order. Fix urgent items now, schedule the rest when it’s convenient.",
              },
              {
                n: "3",
                title: "We keep you protected",
                body:
                  "Choose a maintenance plan to stay ahead of clogs and leaks—with priority scheduling if something breaks.",
              },
            ].map((x) => (
              <Col lg={4} key={x.n}>
                <Card className="border-0 shadow-sm rounded-4 h-100" style={{ background: how.cardBg, border: `1px solid ${how.cardBorder}` }}>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Badge bg="success" className="rounded-pill">
                        {x.n}
                      </Badge>
                      <div className="fw-bold">{x.title}</div>
                    </div>
                    <div style={{ color: how.subtleText }}>{x.body}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className="g-4 mt-1">
            <Col lg={7}>
              <Card className="border-0 shadow-sm rounded-4 h-100" style={{ background: how.cardBg, border: `1px solid ${how.cardBorder}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <StarFill color={how.accent} />
                    <div className="fw-bold">What you get in a proactive checkup</div>
                  </div>

                  <Row className="g-3 mt-1">
                    <Col md={6}>
                      <ListGroup variant="flush">
                        {["Shutoff valve check + accessibility review", "Toilet & fixture leak check", "Water pressure screening", "Visible supply line inspection"].map(
                          (t) => (
                            <ListGroup.Item key={t} className="px-0" style={{ background: "transparent", color: how.text }}>
                              <CheckCircleFill className="me-2" color={how.accent} />
                              {t}
                            </ListGroup.Item>
                          )
                        )}
                      </ListGroup>
                    </Col>
                    <Col md={6}>
                      <ListGroup variant="flush">
                        {["Water heater basic health + safety review", "Drain flow checks (as applicable)", "Under-sink + exposed piping check", "Simple prevention recommendations"].map(
                          (t) => (
                            <ListGroup.Item key={t} className="px-0" style={{ background: "transparent", color: how.text }}>
                              <CheckCircleFill className="me-2" color={how.accent} />
                              {t}
                            </ListGroup.Item>
                          )
                        )}
                      </ListGroup>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="border-0 shadow-sm rounded-4 h-100" style={{ background: how.cardBg, border: `1px solid ${how.cardBorder}` }}>
                <Card.Body className="p-4">
                  <div className="fw-bold mb-2">Perfect for:</div>
                  <ListGroup variant="flush">
                    {[
                      "Homeowners who want fewer surprises",
                      "New home purchases (before problems show up)",
                      "Rental properties & turnovers",
                      "Older homes with mixed plumbing updates",
                      "Anyone who’s had a water damage scare",
                    ].map((t) => (
                      <ListGroup.Item key={t} className="px-0" style={{ background: "transparent", color: how.text }}>
                        <CheckCircleFill className="me-2" color={how.accent} />
                        {t}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>

                  {ENABLE_LEAD_FORM ? (
                    <Button variant="primary" className="rounded-4 w-100 mt-3" onClick={() => scrollToId("lead")}>
                      Service Request
                    </Button>
                  ) : (
                    <Button variant="primary" className="rounded-4 w-100 mt-3" href={BRAND.phoneHref}>
                      Call {BRAND.phoneDisplay}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* PLANS */}
      <section id="plans" style={{ background: plans.bg, color: plans.text }}>
        <Container className="py-5">
          <Row className="align-items-end g-3 mb-3">
            <Col md={8}>
              <h2 className="fw-bold mb-1">Proactive Maintenance Plans</h2>
              <div style={{ color: plans.subtleText }}>
                Simple options. Choose checkup-only, or a plan that keeps you covered year-round.
              </div>
            </Col>
            <Col md={4} className="text-md-end">
              {ENABLE_LEAD_FORM ? (
                <Button variant="outline-dark" className="rounded-4" onClick={() => scrollToId("lead")}>
                  Get plan pricing
                </Button>
              ) : (
                <Button variant="outline-light" className="rounded-4" href={BRAND.phoneHref}>
                  Call for pricing
                </Button>
              )}
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={4}>
              <Card className="border-0 shadow-sm rounded-4 h-100" style={{ background: plans.cardBg, border: `1px solid ${plans.cardBorder}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold h5 mb-1">Checkup Only</div>
                      <div className="small" style={{ color: plans.subtleText }}>
                        One-time inspection + action plan
                      </div>
                    </div>
                    <Badge bg="light" text="dark" className="border">
                      Popular
                    </Badge>
                  </div>
                  <hr style={{ borderColor: plans.divider }} />
                  <ListGroup variant="flush">
                    {["Proactive inspection checklist", "Clear recommended fixes", "Photo notes (when helpful)", "Scheduling follow-up options"].map((t) => (
                      <ListGroup.Item key={t} className="px-0" style={{ background: "transparent", color: plans.text }}>
                        <CheckCircleFill className="me-2" />
                        {t}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  {ENABLE_LEAD_FORM ? (
                    <Button className="rounded-4 w-100 mt-3" variant="outline-dark" onClick={() => scrollToId("lead")}>
                      Service Request
                    </Button>
                  ) : (
                    <Button className="rounded-4 w-100 mt-3" variant="outline-dark" href={BRAND.phoneHref}>
                      Call {BRAND.phoneDisplay}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="border-0 shadow rounded-4 h-100" style={{ background: plans.cardBg, border: `2px solid ${plans.cardBorder}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold h5 mb-1">Home Shield Plan</div>
                      <div className="small" style={{ color: plans.subtleText }}>
                        Best for homeowners
                      </div>
                    </div>
                    <Badge bg="warning" text="dark" className="rounded-pill">
                      Best Value
                    </Badge>
                  </div>
                  <hr style={{ borderColor: plans.divider }} />
                  <ListGroup variant="flush">
                    {["Seasonal checkups (2x per year)", "Priority scheduling", "Member-only service discounts", "Annual water heater service option", "Basic leak-risk review"].map(
                      (t) => (
                        <ListGroup.Item key={t} className="px-0" style={{ background: "transparent", color: plans.text }}>
                          <CheckCircleFill className="text-success me-2" />
                          {t}
                        </ListGroup.Item>
                      )
                    )}
                  </ListGroup>
                  {ENABLE_LEAD_FORM ? (
                    <Button className="rounded-4 w-100 mt-3" variant="dark" onClick={() => scrollToId("lead")}>
                      Service Request
                    </Button>
                  ) : (
                    <Button className="rounded-4 w-100 mt-3" variant="dark" href={BRAND.phoneHref}>
                      Call {BRAND.phoneDisplay}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="border-0 shadow-sm rounded-4 h-100" style={{ background: plans.cardBg, border: `1px solid ${plans.cardBorder}` }}>
                <Card.Body className="p-4">
                  <div className="fw-bold h5 mb-1">Property Partner</div>
                  <div className="small" style={{ color: plans.subtleText }}>
                    For rentals & small portfolios
                  </div>
                  <hr style={{ borderColor: plans.divider }} />
                  <ListGroup variant="flush">
                    {["Turnover & annual inspections", "Multi-unit scheduling support", "Priority repairs when tenants call", "Standardized reporting checklist", "Preventive drain care options"].map(
                      (t) => (
                        <ListGroup.Item key={t} className="px-0" style={{ background: "transparent", color: plans.text }}>
                          <CheckCircleFill className="me-2" />
                          {t}
                        </ListGroup.Item>
                      )
                    )}
                  </ListGroup>
                  {ENABLE_LEAD_FORM ? (
                    <Button className="rounded-4 w-100 mt-3" variant="outline-dark" onClick={() => scrollToId("lead")}>
                      Service Request
                    </Button>
                  ) : (
                    <Button className="rounded-4 w-100 mt-3" variant="outline-dark" href={BRAND.phoneHref}>
                      Call {BRAND.phoneDisplay}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ background: testimonials.bg, color: testimonials.text }}>
        <Container className="py-5">
          <Row className="g-4 mb-3">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Customers love the “no surprises” approach</h2>
              <div style={{ color: testimonials.subtleText }}>
                Clear communication. Proactive recommendations. And help fast when something breaks.
              </div>
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={6}>
              <Card className="border-0 shadow-sm rounded-4 h-100" style={{ background: testimonials.cardBg, border: `1px solid ${testimonials.cardBorder}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <StarFill color={testimonials.accent} />
                    <div className="fw-bold">Homeowner</div>
                  </div>
                  <div style={{ color: testimonials.subtleText }}>
                    “They found a slow leak before it became a ceiling stain. Super clear explanation, zero pressure, and the fix was quick.”
                  </div>
                  <div className="mt-3 small fw-semibold">— Verified Customer</div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6}>
              <Card className="border-0 shadow-sm rounded-4 h-100" style={{ background: testimonials.cardBg, border: `1px solid ${testimonials.cardBorder}` }}>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <StarFill color={testimonials.accent} />
                    <div className="fw-bold">Property Manager</div>
                  </div>
                  <div style={{ color: testimonials.subtleText }}>
                    “The checklist-style reporting is exactly what we needed for our rentals. Preventive inspections saved us multiple emergency calls.”
                  </div>
                  <div className="mt-3 small fw-semibold">— Property Manager</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex justify-content-center mt-4">
            {ENABLE_LEAD_FORM ? (
              <Button variant="primary" className="rounded-4" onClick={() => scrollToId("lead")}>
                Service Request
              </Button>
            ) : (
              <Button variant="primary" className="rounded-4" href={BRAND.phoneHref}>
                Call {BRAND.phoneDisplay}
              </Button>
            )}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ background: faq.bg, color: faq.text }}>
        <Container className="py-5">
          <Row className="mb-3">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">FAQ</h2>
              <div style={{ color: faq.subtleText }}>Quick answers about proactive plumbing and maintenance plans.</div>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm rounded-4" style={{ background: faq.cardBg, border: `1px solid ${faq.cardBorder}` }}>
            <Card.Body className="p-0">
              <Accordion className="rounded-4 overflow-hidden">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>What’s the difference between proactive and emergency plumbing?</Accordion.Header>
                  <Accordion.Body style={{ color: faq.subtleText }}>
                    Emergency plumbing fixes damage already happening. Proactive plumbing focuses on preventing damage by identifying failure points early.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>How long does a proactive checkup take?</Accordion.Header>
                  <Accordion.Body style={{ color: faq.subtleText }}>
                    Most checkups take 45–90 minutes depending on home size, number of bathrooms, and accessibility of plumbing areas.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                  <Accordion.Header>Do you provide a report?</Accordion.Header>
                  <Accordion.Body style={{ color: faq.subtleText }}>
                    Yes—simple, readable notes with prioritized recommendations: urgent, soon, and optional.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                  <Accordion.Header>What if I need help right now?</Accordion.Header>
                  <Accordion.Body style={{ color: faq.subtleText }}>
                    If you have an active leak or flooding risk, call{" "}
                    <a className="fw-semibold" href={BRAND.phoneHref}>
                      {BRAND.phoneDisplay}
                    </a>{" "}
                    and select “Emergency” when you submit the form.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Card.Body>
          </Card>
        </Container>
      </section>

      {/* FOOTER */}
      <section style={{ background: footer.bg, color: footer.text }}>
        <Container className="py-4">
          <Row className="align-items-center g-3">
            <Col md={8}>
              <div className="fw-bold d-flex align-items-center gap-2">
                <DropletFill color={footer.accent} />
                {BRAND.name}
              </div>
              <div className="small" style={{ color: footer.subtleText }}>
                Proactive plumbing checkups • Preventive maintenance plans • Priority scheduling
              </div>
            </Col>
            <Col md={4} className="text-md-end">
              <Button variant="primary" className="rounded-4" href={BRAND.phoneHref}>
                <TelephoneFill className="me-2" />
                Call {BRAND.phoneDisplay}
              </Button>
            </Col>
          </Row>

          <hr style={{ borderColor: footer.divider }} />

          <div className="small" style={{ color: footer.subtleText }}>
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </div>
        </Container>
      </section>
    </div>
  );
}

export default ProactivePlumber_Landing_2025_12_23_chat_v09;
